/**
 * SocialDeepDive Plugin - Platform Definitions
 *
 * Defines all 12 supported social media platforms with their metadata.
 * Each platform has a unique ID, display name, icon, color, and custom terminology.
 */

import { SocialPlatform } from './types';
import {
  FaInstagram,
  FaFacebook,
  FaYoutube,
  FaTiktok,
  FaLinkedin,
  FaReddit,
  FaTumblr,
  FaSlack,
  FaVimeo,
  FaBehance,
  FaYelp,
  FaFlickr,
} from 'react-icons/fa';

/**
 * All 12 supported social media platforms
 *
 * Icons from react-icons/fa (Font Awesome brand icons)
 */
export const SOCIAL_PLATFORMS: SocialPlatform[] = [
  {
    id: 'instagram',
    name: 'Instagram',
    icon: FaInstagram,
    color: '#E1306C',
    followersTerm: 'Followers',
    impressionsTerm: 'Impressions',
    engagementsTerm: 'Engagements',
  },
  {
    id: 'facebook',
    name: 'Facebook',
    icon: FaFacebook,
    color: '#1877F2',
    followersTerm: 'Followers',
    impressionsTerm: 'Impressions',
    engagementsTerm: 'Engagements',
  },
  {
    id: 'youtube',
    name: 'YouTube',
    icon: FaYoutube,
    color: '#FF0000',
    followersTerm: 'Subscribers',
    impressionsTerm: 'Views',
    engagementsTerm: 'Engagements',
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    icon: FaTiktok,
    color: '#000000',
    followersTerm: 'Followers',
    impressionsTerm: 'Impressions',
    engagementsTerm: 'Engagements',
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    icon: FaLinkedin,
    color: '#0A66C2',
    followersTerm: 'Followers',
    impressionsTerm: 'Impressions',
    engagementsTerm: 'Engagements',
  },
  {
    id: 'reddit',
    name: 'Reddit',
    icon: FaReddit,
    color: '#FF4500',
    followersTerm: 'Subscribers',
    impressionsTerm: 'Impressions',
    engagementsTerm: 'Upvotes/Comments',
  },
  {
    id: 'tumblr',
    name: 'Tumblr',
    icon: FaTumblr,
    color: '#35465C',
    followersTerm: 'Followers',
    impressionsTerm: 'Impressions',
    engagementsTerm: 'Reblogs/Likes',
  },
  {
    id: 'slack',
    name: 'Slack',
    icon: FaSlack,
    color: '#4A154B',
    followersTerm: 'Members',
    impressionsTerm: 'Messages Read',
    engagementsTerm: 'Reactions/Replies',
  },
  {
    id: 'vimeo',
    name: 'Vimeo',
    icon: FaVimeo,
    color: '#1AB7EA',
    followersTerm: 'Followers',
    impressionsTerm: 'Views',
    engagementsTerm: 'Engagements',
  },
  {
    id: 'behance',
    name: 'Behance',
    icon: FaBehance,
    color: '#1769FF',
    followersTerm: 'Followers',
    impressionsTerm: 'Project Views',
    engagementsTerm: 'Appreciations',
  },
  {
    id: 'yelp',
    name: 'Yelp',
    icon: FaYelp,
    color: '#D32323',
    followersTerm: 'Followers',
    impressionsTerm: 'Page Views',
    engagementsTerm: 'Reviews/Check-ins',
  },
  {
    id: 'flickr',
    name: 'Flickr',
    icon: FaFlickr,
    color: '#FF0084',
    followersTerm: 'Followers',
    impressionsTerm: 'Photo Views',
    engagementsTerm: 'Favorites/Comments',
  },
];

/**
 * Default enabled platforms (4 most popular)
 * Used when creating initial organization settings
 */
export const DEFAULT_ENABLED_PLATFORMS = ['instagram', 'facebook', 'youtube', 'tiktok'];

/**
 * Get platform by ID
 */
export function getPlatformById(id: string): SocialPlatform | undefined {
  return SOCIAL_PLATFORMS.find((p) => p.id === id);
}

/**
 * Get multiple platforms by IDs
 */
export function getPlatformsByIds(ids: string[]): SocialPlatform[] {
  return ids.map((id) => getPlatformById(id)).filter((p): p is SocialPlatform => p !== undefined);
}

/**
 * Get all platform IDs
 */
export function getAllPlatformIds(): string[] {
  return SOCIAL_PLATFORMS.map((p) => p.id);
}

/**
 * Check if a platform ID is valid
 */
export function isValidPlatformId(id: string): boolean {
  return SOCIAL_PLATFORMS.some((p) => p.id === id);
}

/**
 * Validate array of platform IDs
 * Returns only valid platform IDs, filtering out invalid ones
 */
export function validatePlatformIds(ids: string[]): string[] {
  return ids.filter(isValidPlatformId);
}

/**
 * Get platforms sorted by popularity (default order)
 */
export function getPlatformsSortedByPopularity(): SocialPlatform[] {
  // Already sorted by popularity in the SOCIAL_PLATFORMS array
  return [...SOCIAL_PLATFORMS];
}

/**
 * Get platforms sorted alphabetically by name
 */
export function getPlatformsSortedAlphabetically(): SocialPlatform[] {
  return [...SOCIAL_PLATFORMS].sort((a, b) => a.name.localeCompare(b.name));
}
